import { FC, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, Datepicker } from '@ibsys/kit';
import { useConversationsAttributes } from '@/entities/conversations';
import type { ConversationAttributeItem } from '@/entities/attributes/model/api/types';
import { useTimeDisplayFormat } from '@/entities/settings';
import type { AttributeFilter } from '@/entities/dashboards';
import { AttributeFilterCell, AttributeFilterPicker, createEmptyFilter } from '@/features/attribute-filters';

/**
 * Значение сквозного фильтра «Стратега».
 * `filters` — атрибутные фильтры в формате бэка (см. {@link AttributeFilter}),
 * `dateFrom`/`dateTo` — границы периода в формате YYYY-MM-DD (или не заданы).
 */
export type StrategistFiltersValue = {
  filters: AttributeFilter[];
  dateFrom?: string;
  dateTo?: string;
};

type Props = {
  /** Текущее значение фильтра */
  value: StrategistFiltersValue;
  /** Колбэк обновления всего значения фильтра */
  onChange: (next: StrategistFiltersValue) => void;
};

/**
 * Сквозной фильтр раздела «Стратег»: период выборки и произвольные атрибутные условия.
 *
 * Период — единственный собственный контрол панели: он отбирает по дате самого диалога
 * (`strategist_dialog.date`), а не по значению атрибута, и через «Добавить атрибут» не
 * набирается. Всё остальное — атрибутные условия, добавляемые вручную: `value.filters`
 * целиком состоит из них, исключённых путей нет.
 *
 * Поля значений рисует `AttributeFilterCell` — то же поле, что и отбор по колонке атрибута
 * в гриде диалогов. Наружу отдаётся `{ filters, dateFrom, dateTo }` — готовое тело POST-запросов
 * «Стратега»; незаполненные условия отсекаются на границе запроса (`entities/strategist`).
 */
export const StrategistFilters: FC<Props> = ({ value, onChange }) => {
  const { t } = useTranslation('strategist');
  // Формат интервалов времени задаёт вид отбора по атрибуту-интервалу — тот же, что в таблицах
  const timeDisplayFormat = useTimeDisplayFormat('filter');

  // Системная конфигурация атрибутов разговора — те же, что в гриде диалогов и дашборде качества.
  const { data: conversationAttributes } = useConversationsAttributes({ limit: 1000 });
  const availableAttributes: ConversationAttributeItem[] = useMemo(
    () => conversationAttributes?.attributes ?? [],
    [conversationAttributes?.attributes],
  );

  // Карта атрибутов по `path` — единый ключ совпадает с `path` в атрибутных фильтрах.
  const attributesByPath = useMemo(() => {
    const map = new Map<string, ConversationAttributeItem>();
    availableAttributes.forEach((a) => map.set(a.path, a));
    return map;
  }, [availableAttributes]);

  // Уже добавленные пути скрыты из списка добавления, чтобы избежать дублей путей.
  const activePaths = useMemo(() => new Set(value.filters.map((f) => f.path)), [value.filters]);

  const handleAddAttribute = (path: string) => {
    const attribute = attributesByPath.get(path);
    if (!attribute) return;
    const newFilter = createEmptyFilter(attribute, timeDisplayFormat);
    if (!newFilter) return;
    onChange({ ...value, filters: [...value.filters, newFilter] });
  };

  const handleUpdateFilter = (next: AttributeFilter) => {
    onChange({ ...value, filters: value.filters.map((f) => (f.path === next.path ? next : f)) });
  };

  const handleRemoveFilter = (path: string) => {
    onChange({ ...value, filters: value.filters.filter((f) => f.path !== path) });
  };

  return (
    <Card>
      <CardContent className="p-4 flex flex-col gap-4">
        <div className="flex flex-wrap gap-4 items-end w-full">
          <Datepicker
            mode="range"
            outputType="iso-date"
            label={t('filters.period')}
            value={{ from: value.dateFrom, to: value.dateTo }}
            onChange={(v) => {
              const range = typeof v === 'object' && v !== null ? v : { from: undefined, to: undefined };
              onChange({ ...value, dateFrom: range.from || undefined, dateTo: range.to || undefined });
            }}
          />

          <AttributeFilterPicker
            availableAttributes={availableAttributes}
            activePaths={activePaths}
            onAdd={handleAddAttribute}
          />
        </div>

        {value.filters.length > 0 && (
          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-3 items-end">
            {value.filters.map((filter) => (
              <AttributeFilterCell
                key={filter.path}
                attribute={attributesByPath.get(filter.path)}
                filter={filter}
                onChange={handleUpdateFilter}
                onRemove={() => handleRemoveFilter(filter.path)}
              />
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
};
